import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService, Policy } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingOfficeIcon,
  UserIcon,
  FolderIcon,
  PrinterIcon,
  CameraIcon,
  ClipboardDocumentIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  FolderPlusIcon,
  DocumentDuplicateIcon,
  PlusCircleIcon,
  ArrowRightCircleIcon,
  ArrowPathIcon,
  KeyIcon
} from '@heroicons/react/24/outline';

interface UserPreferences {
  id: string;
  user_id: string;
  theme: string;
  language: string;
  timezone: string;
  email_notifications: boolean;
  desktop_notifications: boolean;
  web_notifications: boolean;
  require_2fa: boolean;
  backup_master_keys: boolean;
  allow_multi_device_access: boolean;
  session_timeout_value: number;
  session_timeout_unit: 'minutes' | 'hours';
  quick_encrypt_policy_file: string;
  quick_encrypt_policy_folder: string;
  created_at: string;
  updated_at: string;
}

const PolicyTab: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const [companyPolicies, setCompanyPolicies] = useState<Policy[]>([]);
  const [userPolicies, setUserPolicies] = useState<Policy[]>([]);
  const [systemPolicies, setSystemPolicies] = useState<Policy[]>([]);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [companySecuritySettings, setCompanySecuritySettings] = useState<{
    quick_encrypt_policy_file?: string;
    quick_encrypt_policy_folder?: string;
    quick_encrypt_policy_override?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<{
    company: boolean;
    user: boolean;
    system: boolean;
  }>({
    company: true,
    user: true,
    system: false,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load company security settings if user belongs to a company (for both admins and regular users)
      let companySettingsPromise: Promise<any> = Promise.resolve(null);
      if (user?.company_id) {
        companySettingsPromise = apiService.getCompanySecuritySettings(user.company_id).catch(() => null);
      }

      const [policiesResponse, userPoliciesResponse, preferencesData, companySettings] = await Promise.all([
        apiService.getPolicies(),
        apiService.getUserPolicies(),
        apiService.getUserPreferences(),
        companySettingsPromise
      ]);

      console.log('🔍 PolicyTab: Loaded data:', {
        policiesResponse,
        userPoliciesResponse,
        preferences: preferencesData
      });

      // Handle API response structure - could be array or object with policies property
      let policies: Policy[] = [];
      if (Array.isArray(policiesResponse)) {
        policies = policiesResponse;
      } else if (policiesResponse && typeof policiesResponse === 'object') {
        const responseObj = policiesResponse as { policies?: Policy[] };
        if ('policies' in responseObj && Array.isArray(responseObj.policies)) {
          policies = responseObj.policies;
        }
      }

      let userPoliciesData: Policy[] = [];
      if (Array.isArray(userPoliciesResponse)) {
        userPoliciesData = userPoliciesResponse;
      } else if (userPoliciesResponse && typeof userPoliciesResponse === 'object') {
        const responseObj = userPoliciesResponse as { policies?: Policy[] };
        if ('policies' in responseObj && Array.isArray(responseObj.policies)) {
          userPoliciesData = responseObj.policies;
        }
      }

      // Deduplicate policies by ID to prevent duplicates
      const deduplicatedPolicies = new Map<string, Policy>();
      policies.forEach(p => {
        if (!deduplicatedPolicies.has(p.id)) {
          deduplicatedPolicies.set(p.id, p);
        }
      });
      const uniquePolicies = Array.from(deduplicatedPolicies.values());

      // Filter policies by type
      const companyPols = uniquePolicies.filter(p => p.type === 'company');
      const userPols = uniquePolicies.filter(p => p.type === 'user');
      const systemPols = uniquePolicies.filter(p => p.type === 'system');

      // Also include user-created policies from getUserPolicies endpoint, but deduplicate
      const allUserPols = [...userPols];
      userPoliciesData.forEach(p => {
        if (!allUserPols.find(up => up.id === p.id)) {
          allUserPols.push(p);
        }
      });

      setCompanyPolicies(companyPols);
      setUserPolicies(allUserPols);
      setSystemPolicies(systemPols);
      
      // Handle the nested preferences structure from the API response
      const prefs = preferencesData.preferences || preferencesData;
      setUserPreferences(prefs);
      
      // Set company security settings if available
      if (companySettings) {
        setCompanySecuritySettings({
          quick_encrypt_policy_file: companySettings.quick_encrypt_policy_file,
          quick_encrypt_policy_folder: companySettings.quick_encrypt_policy_folder,
          quick_encrypt_policy_override: companySettings.quick_encrypt_policy_override,
        });
        
        // Debug: Log company security settings
        console.log('🔍 PolicyTab: Company security settings loaded:', {
          quick_encrypt_policy_file: companySettings.quick_encrypt_policy_file,
          quick_encrypt_policy_folder: companySettings.quick_encrypt_policy_folder,
          quick_encrypt_policy_override: companySettings.quick_encrypt_policy_override,
        });
      } else if (user?.company_id) {
        console.log('⚠️ PolicyTab: User has company_id but company settings not loaded:', user.company_id);
      }
      
      console.log('🔍 PolicyTab: Filtered policies:', {
        company: companyPols.length,
        user: allUserPols.length,
        system: systemPols.length
      });
    } catch (err) {
      console.error('Failed to load policy data:', err);
      setError(t('policies.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleQuickEncryptPolicyChange = async (policyId: string, assetType: 'file' | 'folder') => {
    // For admins, update company settings
    if (user?.role === 'admin' && user?.company_id) {
      await handleSetAsQuickEncrypt(policyId, assetType);
      return;
    }

    // For regular users, update user preferences
    if (!userPreferences) return;
    
    // If company override is enabled, still allow update but warn user
    const overrideEnabled = companySecuritySettings?.quick_encrypt_policy_override;
    if (overrideEnabled) {
      // Still allow the update - it will be saved but won't take effect until override is disabled
      // This allows users to set their preference for when override is later disabled
    }

    try {
      setSaving(true);
      setError(null);

      const updatedPreferences: any = {};
      if (assetType === 'file') {
        updatedPreferences.quick_encrypt_policy_file = policyId;
      } else {
        updatedPreferences.quick_encrypt_policy_folder = policyId;
      }

      const response = await apiService.updateUserPreferences(updatedPreferences);
      
      // Handle the nested preferences structure from the API response
      const prefs = response.preferences || response;
      setUserPreferences(prefs);
      
      console.log(`✅ PolicyTab: Updated quick encrypt policy for ${assetType} to:`, policyId);
      
      if (overrideEnabled) {
        toast.success(t('policies.quickEncryptSaved', { assetType }));
      } else {
        toast.success(t('policies.quickEncryptUpdated', { assetType }));
      }
    } catch (err) {
      console.error('Failed to update quick encrypt policy:', err);
      setError(t('policies.updateFailed'));
      toast.error(t('policies.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Handle setting a policy as quick encrypt (for admins - updates company settings)
  const handleSetAsQuickEncrypt = async (policyId: string, assetType: 'file' | 'folder') => {
    if (!user?.company_id || user?.role !== 'admin') return;

    try {
      setSaving(true);
      setError(null);

      const updateData: any = {};
      if (assetType === 'file') {
        updateData.quick_encrypt_policy_file = policyId;
      } else {
        updateData.quick_encrypt_policy_folder = policyId;
      }

      await apiService.updateCompanySecuritySettings(user.company_id, updateData, false);
      
      // Reload company settings
      const settings = await apiService.getCompanySecuritySettings(user.company_id);
      setCompanySecuritySettings({
        quick_encrypt_policy_file: settings.quick_encrypt_policy_file,
        quick_encrypt_policy_folder: settings.quick_encrypt_policy_folder,
        quick_encrypt_policy_override: settings.quick_encrypt_policy_override,
      });
      
      toast.success(t('policies.setAsDefaultQuickEncrypt', { assetType }));
    } catch (err: any) {
      console.error('Failed to set quick encrypt policy:', err);
      setError(t('policies.setDefaultFailed'));
      toast.error(err.response?.data?.error || t('policies.setDefaultFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Handle toggle for quick encrypt policy override (for admins)
  const handleToggleOverride = async (enabled: boolean) => {
    if (!user?.company_id || user?.role !== 'admin') return;

    try {
      setSaving(true);
      setError(null);

      await apiService.updateCompanySecuritySettings(
        user.company_id,
        { quick_encrypt_policy_override: enabled },
        false
      );
      
      // Reload company settings
      const settings = await apiService.getCompanySecuritySettings(user.company_id);
      setCompanySecuritySettings({
        quick_encrypt_policy_file: settings.quick_encrypt_policy_file,
        quick_encrypt_policy_folder: settings.quick_encrypt_policy_folder,
        quick_encrypt_policy_override: settings.quick_encrypt_policy_override,
      });
      
      toast.success(enabled ? t('policies.overrideEnabled') : t('policies.overrideDisabled'));
    } catch (err: any) {
      console.error('Failed to toggle override:', err);
      setError(t('policies.overrideUpdateFailed'));
      toast.error(err.response?.data?.error || t('policies.overrideUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section: 'company' | 'user' | 'system') => {
    const wasExpanded = expandedSections[section];
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    
    // Auto-scroll when expanding System Policies accordion
    if (section === 'system' && !wasExpanded) {
      setTimeout(() => {
        const systemSection = document.getElementById('system-policies-section');
        if (systemSection) {
          systemSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  };

  const getPolicyIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'encrypt':
        return <ShieldCheckIcon className="w-5 h-5 text-green-600" />;
      case 'secure':
        return <ShieldCheckIcon className="w-5 h-5 text-blue-600" />;
      case 'restricted':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />;
      default:
        return <DocumentTextIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Active
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
            <ClockIcon className="w-3 h-3 mr-1" />
            Inactive
          </span>
        );
      case 'draft':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <DocumentTextIcon className="w-3 h-3 mr-1" />
            Draft
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get permission icons for a policy
  const getPermissionIcons = (policy: Policy) => {
    const permissions: Array<{ icon: React.ComponentType<{ className?: string }>, label: string, allowed: boolean }> = [];
    const settings = policy.settings;

    if (policy.asset_type === 'file' && settings.file_permissions) {
      const fp = settings.file_permissions;
      permissions.push(
        { icon: ArrowPathIcon, label: 'Convert Original', allowed: fp.convert_original?.allowed || false },
        { icon: PencilIcon, label: 'Save Changes', allowed: fp.save_changes?.allowed || false },
        { icon: ClipboardDocumentIcon, label: 'Copy/Paste', allowed: fp.copy_paste?.allowed || false },
        { icon: CameraIcon, label: 'Screen Capture', allowed: fp.screen_capture?.allowed || false },
        { icon: PrinterIcon, label: 'Print', allowed: fp.print?.allowed || false },
        { icon: UserGroupIcon, label: 'Share', allowed: fp.share?.allowed || false }
      );
    } else if (policy.asset_type === 'folder' && settings.folder_permissions) {
      const fop = settings.folder_permissions;
      permissions.push(
        { icon: FolderPlusIcon, label: 'Create', allowed: fop.create?.allowed || false },
        { icon: DocumentDuplicateIcon, label: 'Copy', allowed: fop.copy?.allowed || false },
        { icon: PlusCircleIcon, label: 'Add', allowed: fop.add?.allowed || false },
        { icon: ArrowRightCircleIcon, label: 'Move', allowed: fop.move?.allowed || false },
        { icon: UserGroupIcon, label: 'Share', allowed: fop.share?.allowed || false },
        { icon: TrashIcon, label: 'Delete', allowed: fop.delete?.allowed || false }
      );
    }

    // Add encryption indicator
    if (settings.encryption_type) {
      permissions.unshift({ icon: KeyIcon, label: `Encryption: ${settings.encryption_type}`, allowed: true });
    }

    return permissions;
  };

  // Get all available policies for dropdown, filtered by asset type and user role
  const getAvailablePoliciesForAssetType = (assetType: 'file' | 'folder') => {
    let availablePolicies: Policy[] = [];
    
    if (user?.role === 'sysadmin' || user?.role === 'admin') {
      // Admins can see: system, company, and user policies
      availablePolicies = [...systemPolicies, ...companyPolicies, ...userPolicies];
    } else {
      // Regular users can see: system, company, and user policies
      // System policies are included so users can see them if set as company defaults
      // They may not be selectable if override is enabled, but they should be visible
      availablePolicies = [...systemPolicies, ...companyPolicies, ...userPolicies];
    }
    
    // Deduplicate by ID to prevent duplicates
    const deduplicated = new Map<string, Policy>();
    availablePolicies.forEach(p => {
      if (!deduplicated.has(p.id)) {
        deduplicated.set(p.id, p);
      }
    });
    const uniquePolicies = Array.from(deduplicated.values());
    
    // Filter by asset type
    return uniquePolicies.filter(p => p.asset_type === assetType);
  };
  
  // Get effective quick encrypt policy (company default, with user customization if allowed)
  const getEffectiveQuickEncryptPolicy = (assetType: 'file' | 'folder'): string => {
    // For admins, always return company policy (they're setting company defaults)
    if (user?.role === 'admin') {
      if (companySecuritySettings) {
        if (assetType === 'file') {
          return companySecuritySettings.quick_encrypt_policy_file || '';
        } else {
          return companySecuritySettings.quick_encrypt_policy_folder || '';
        }
      }
      return '';
    }
    
    // For regular users, check company settings first
    let companyPolicy = '';
    if (companySecuritySettings) {
      if (assetType === 'file') {
        companyPolicy = companySecuritySettings.quick_encrypt_policy_file || '';
      } else {
        companyPolicy = companySecuritySettings.quick_encrypt_policy_folder || '';
      }
    }
    
    // If override is enabled, always use company policy (locked)
    if (companySecuritySettings?.quick_encrypt_policy_override) {
      return companyPolicy;
    }
    
    // If override is disabled, check if user has customized it
    // If user has a preference, use that; otherwise use company default
    if (userPreferences) {
      const userPolicy = assetType === 'file' 
        ? userPreferences.quick_encrypt_policy_file || ''
        : userPreferences.quick_encrypt_policy_folder || '';
      
      // If user has set a preference, use it; otherwise fall back to company default
      return userPolicy || companyPolicy;
    }
    
    // No user preferences, use company default
    return companyPolicy;
  };
  
  // Check if user can edit quick encrypt policies
  // Admins can always edit (they're setting company defaults)
  // Regular users can only edit if override is disabled (false or undefined)
  const canEditQuickEncryptPolicies = user?.role === 'admin' || !companySecuritySettings?.quick_encrypt_policy_override;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{t('policies.error')}</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Encrypt Policy Selection */}
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2" />
            {t('policies.quickEncryptPolicies')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            {user?.role === 'admin' 
              ? t('policies.descriptionCompany')
              : t('policies.descriptionUser')}
          </p>
    
          {user?.role !== 'admin' && companySecuritySettings && companySecuritySettings.quick_encrypt_policy_override && (
            <div className="mt-3 rounded-lg p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                {t('policies.policiesSetAsDefaults')}
              </p>
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-5 sm:p-0">
          <div className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-800">
            {/* Override Toggle (Admin Only) */}
            {user?.role === 'admin' && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                  <ShieldCheckIcon className="w-4 h-4 mr-2" />
                  {t('policies.preventOverride')}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {t('policies.preventOverrideDescription')}
                      </p>
                   
                    </div>
                    <button
                      onClick={() => handleToggleOverride(!companySecuritySettings?.quick_encrypt_policy_override)}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                        companySecuritySettings?.quick_encrypt_policy_override
                          ? 'bg-governate-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={companySecuritySettings?.quick_encrypt_policy_override}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          companySecuritySettings?.quick_encrypt_policy_override ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </dd>
              </div>
            )}
            
            {/* File Policy Selection */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t('policies.filePolicy')}
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                <select
                  value={getEffectiveQuickEncryptPolicy('file')}
                  onChange={(e) => handleQuickEncryptPolicyChange(e.target.value, 'file')}
                  disabled={saving || !canEditQuickEncryptPolicies}
                  className={`block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-governate-500 focus:ring-governate-500 sm:text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                    !canEditQuickEncryptPolicies ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''
                  }`}
                >
                  <option value="">{t('policies.noDefaultPolicy')}</option>
                  {getAvailablePoliciesForAssetType('file').map((policy) => (
                    <option key={policy.id} value={policy.id}>
                      {policy.name} - {policy.description} {policy.type !== 'system' ? '(Custom)' : ''}
                    </option>
                  ))}
                </select>
                {saving && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('policies.saving')}</p>
                )}
              </dd>
            </div>
            
            {/* Folder Policy Selection */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                <FolderIcon className="w-4 h-4 mr-2" />
                {t('policies.folderPolicy')}
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                <select
                  value={getEffectiveQuickEncryptPolicy('folder')}
                  onChange={(e) => handleQuickEncryptPolicyChange(e.target.value, 'folder')}
                  disabled={saving || !canEditQuickEncryptPolicies}
                  className={`block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-governate-500 focus:ring-governate-500 sm:text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                    !canEditQuickEncryptPolicies ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''
                  }`}
                >
                  <option value="">{t('policies.noDefaultPolicy')}</option>
                  {getAvailablePoliciesForAssetType('folder').map((policy) => (
                    <option key={policy.id} value={policy.id}>
                      {policy.name} - {policy.description} {policy.type !== 'system' ? '(Custom)' : ''}
                    </option>
                  ))}
                </select>
                {saving && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('policies.saving')}</p>
                )}
              </dd>
            </div>
          </div>
        </div>
      </div>

      {/* User Policies (only for non-admin users) - Show first for regular users */}
      {user?.role !== 'admin' && userPolicies.length > 0 && (
        <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
          <button
            onClick={() => toggleSection('user')}
            className="w-full px-4 py-5 sm:px-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
          >
            <div className="flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                {t('policies.yourCustomPolicies')}
              </h3>
              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                {userPolicies.length}
              </span>
            </div>
            {expandedSections.user ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
          {expandedSections.user && (
            <div className="border-t border-gray-200 dark:border-gray-800">
              <div className="divide-y divide-gray-200 dark:divide-gray-800">
                {userPolicies.map((policy) => {
                  const permissions = getPermissionIcons(policy);
                  return (
                    <div key={policy.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-900">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          {getPolicyIcon(policy.type)}
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">{policy.name}</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{policy.description}</p>
                            <div className="mt-2 flex items-center space-x-2 flex-wrap">
                              {permissions.map((perm, idx) => {
                                const Icon = perm.icon;
                                return (
                                  <div
                                    key={idx}
                                    className="group relative"
                                    title={perm.label + (perm.allowed ? ' (Allowed)' : ' (Not Allowed)')}
                                  >
                                    <Icon
                                      className={`w-4 h-4 ${
                                        perm.allowed
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-gray-300 dark:text-gray-600'
                                      }`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-400 dark:text-gray-500">
                              <span>Type: {policy.type}</span>
                              <span>Asset: {policy.asset_type}</span>
                              <span>Created: {formatDate(policy.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {getStatusBadge(policy.status)}
                          {(getEffectiveQuickEncryptPolicy(policy.asset_type) === policy.id) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {t('policies.default')}
                            </span>
                          )}
                          {/* Show button for regular users when override is disabled (customization allowed) */}
                          {user?.role !== 'admin' && !companySecuritySettings?.quick_encrypt_policy_override && (
                            <button
                              onClick={() => handleQuickEncryptPolicyChange(policy.id, policy.asset_type as 'file' | 'folder')}
                              disabled={saving || getEffectiveQuickEncryptPolicy(policy.asset_type as 'file' | 'folder') === policy.id}
                              className="ml-2 px-3 py-1 text-xs font-medium rounded-md bg-governate-600 text-white hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title={t('policies.setAsDefaultQuickEncrypt', { assetType: policy.asset_type })}
                            >
                              {t('policies.setAsQuickEncrypt')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Company Policies (for all company users) - Show after User Policies for regular users */}
      {user?.company_id && companyPolicies.length > 0 && (
        <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
          <button
            onClick={() => toggleSection('company')}
            className="w-full px-4 py-5 sm:px-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
          >
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                {t('policies.companyPolicies')}
              </h3>
              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {companyPolicies.length}
              </span>
            </div>
            {expandedSections.company ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
          {expandedSections.company && (
            <div className="border-t border-gray-200 dark:border-gray-800">
              <div className="divide-y divide-gray-200 dark:divide-gray-800">
                {companyPolicies.map((policy) => {
                  const permissions = getPermissionIcons(policy);
                  return (
                    <div key={policy.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-900">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          {getPolicyIcon(policy.type)}
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">{policy.name}</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{policy.description}</p>
                            <div className="mt-2 flex items-center space-x-2 flex-wrap">
                              {permissions.map((perm, idx) => {
                                const Icon = perm.icon;
                                return (
                                  <div
                                    key={idx}
                                    className="group relative"
                                    title={perm.label + (perm.allowed ? ' (Allowed)' : ' (Not Allowed)')}
                                  >
                                    <Icon
                                      className={`w-4 h-4 ${
                                        perm.allowed
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-gray-300 dark:text-gray-600'
                                      }`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-400 dark:text-gray-500">
                              <span>Type: {policy.type}</span>
                              <span>Asset: {policy.asset_type}</span>
                              <span>Created: {formatDate(policy.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {getStatusBadge(policy.status)}
                          {(getEffectiveQuickEncryptPolicy(policy.asset_type) === policy.id) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              Default
                            </span>
                          )}
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => handleSetAsQuickEncrypt(policy.id, policy.asset_type as 'file' | 'folder')}
                              disabled={saving || getEffectiveQuickEncryptPolicy(policy.asset_type as 'file' | 'folder') === policy.id}
                              className="ml-2 px-3 py-1 text-xs font-medium rounded-md bg-governate-600 text-white hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title={t('policies.setAsDefaultQuickEncrypt', { assetType: policy.asset_type })}
                            >
                              {t('policies.setAsQuickEncrypt')}
                            </button>
                          )}
                          {/* Show button for regular users when override is disabled (customization allowed) */}
                          {user?.role !== 'admin' && !companySecuritySettings?.quick_encrypt_policy_override && (
                            <button
                              onClick={() => handleQuickEncryptPolicyChange(policy.id, policy.asset_type as 'file' | 'folder')}
                              disabled={saving || getEffectiveQuickEncryptPolicy(policy.asset_type as 'file' | 'folder') === policy.id}
                              className="ml-2 px-3 py-1 text-xs font-medium rounded-md bg-governate-600 text-white hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title={t('policies.setAsDefaultQuickEncrypt', { assetType: policy.asset_type })}
                            >
                              {t('policies.setAsQuickEncrypt')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Policies */}
      <div id="system-policies-section" className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900">
        <button
          onClick={() => toggleSection('system')}
          className="w-full px-4 py-5 sm:px-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
        >
          <div className="flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              System Policies
            </h3>
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
              {systemPolicies.length}
            </span>
          </div>
          {expandedSections.system ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>
        {expandedSections.system && (
          <div className="border-t border-gray-200 dark:border-gray-800">
            {systemPolicies.length === 0 ? (
              <div className="px-4 py-5 sm:px-6">
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No system policies found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-800">
                {systemPolicies.map((policy) => {
                  const permissions = getPermissionIcons(policy);
                  return (
                    <div key={policy.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-900">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          {getPolicyIcon(policy.type)}
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">{policy.name}</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{policy.description}</p>
                            <div className="mt-2 flex items-center space-x-2 flex-wrap">
                              {permissions.map((perm, idx) => {
                                const Icon = perm.icon;
                                return (
                                  <div
                                    key={idx}
                                    className="group relative"
                                    title={perm.label + (perm.allowed ? ' (Allowed)' : ' (Not Allowed)')}
                                  >
                                    <Icon
                                      className={`w-4 h-4 ${
                                        perm.allowed
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-gray-300 dark:text-gray-600'
                                      }`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-400 dark:text-gray-500">
                              <span>Type: {policy.type}</span>
                              <span>Asset: {policy.asset_type}</span>
                              <span>Created: {formatDate(policy.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {getStatusBadge(policy.status)}
                          {(getEffectiveQuickEncryptPolicy(policy.asset_type) === policy.id) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {t('policies.default')}
                            </span>
                          )}
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => handleSetAsQuickEncrypt(policy.id, policy.asset_type as 'file' | 'folder')}
                              disabled={saving || getEffectiveQuickEncryptPolicy(policy.asset_type as 'file' | 'folder') === policy.id}
                              className="ml-2 px-3 py-1 text-xs font-medium rounded-md bg-governate-600 text-white hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title={t('policies.setAsDefaultQuickEncrypt', { assetType: policy.asset_type })}
                            >
                              {t('policies.setAsQuickEncrypt')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyTab;
